import * as React from 'react'

import { useChat } from '@/contexts/chat-context'

import { Message } from '@/types/chat.type'
import { UserAuth } from '@/types/user.type'

function getTailMessageKey(message: Message | undefined): string | null {
  if (!message) return null
  if (message.tempId) return `temp:${message.tempId}`
  return `id:${message.id}`
}

interface UseChatScrollBehaviorProps {
  scrollManager: {
    scrollToBottom: () => void
    scrollToBottomInstant: () => void
    prepareConversationBottomScroll?: () => void
    setSuppressBottomScroll: (suppress: boolean) => void
    beginMessageNavigationScroll: () => void
    isBottomScrollSuppressed: () => boolean
    isNearBottom: (threshold?: number) => boolean
    maintainScrollPosition: () => void
    restoreScrollPosition: () => void
    scrollToMessage?: (messageId: number) => void
    scrollToMessageWithDelay?: (messageId: number, delay?: number) => void
    scrollToConversation?: (conversationId: number, conversationType?: string) => void
    messagesEndRef?: React.RefObject<HTMLDivElement>
    messagesContainerRef?: React.RefObject<HTMLDivElement>
    conversationListRef?: React.RefObject<HTMLDivElement>
    isConversationBottomSnapRequested?: () => boolean
    clearConversationBottomSnapRequest?: () => void
  }
  isOpen?: boolean
  authUser?: UserAuth | null
}

export const useChatScrollBehavior = ({ scrollManager, isOpen, authUser: _authUser }: UseChatScrollBehaviorProps) => {
  const {
    messages,
    conversation,
    isLoading,
    isLoadingPrevious,
    isLoadingMore,
    isLoadingContextMessages,
    pendingScrollToMessage,
    highlightedMessageId,
  } = useChat()

  const isNavigatingToMessage = Boolean(
    pendingScrollToMessage &&
      (!pendingScrollToMessage.targetConversationId ||
        pendingScrollToMessage.targetConversationId === conversation?.id)
  )

  const isLoadingPreviousRef = React.useRef(false)
  const isLoadingMoreRef = React.useRef(false)
  const scrolledConversationRef = React.useRef<number | null>(null)
  const lastTailMessageKeyRef = React.useRef<string | null>(null)
  const preferInstantScrollRef = React.useRef(false)
  const wasNavigatingToMessageRef = React.useRef(false)
  const viewerAwayFromBottomRef = React.useRef(false)
  const prevConversationIdRef = React.useRef<number | null>(null)

  const shouldBlockAutoScrollToBottom = React.useCallback(() => {
    return (
      isNavigatingToMessage ||
      isLoadingPrevious ||
      isLoadingPreviousRef.current ||
      isLoadingContextMessages ||
      highlightedMessageId != null ||
      scrollManager.isBottomScrollSuppressed()
    )
  }, [
    highlightedMessageId,
    isLoadingContextMessages,
    isLoadingPrevious,
    isNavigatingToMessage,
    scrollManager,
  ])

  const scrollToBottom = React.useCallback(() => {
    if (shouldBlockAutoScrollToBottom()) {
      return
    }
    scrollManager.scrollToBottom()
  }, [scrollManager, shouldBlockAutoScrollToBottom])

  const messagesRef = React.useRef(messages)
  messagesRef.current = messages

  const markConversationScrollHandled = React.useCallback((conversationId: number) => {
    scrolledConversationRef.current = conversationId
    const currentMessages = messagesRef.current
    lastTailMessageKeyRef.current = getTailMessageKey(currentMessages[currentMessages.length - 1])
    preferInstantScrollRef.current = false
  }, [])

  React.useEffect(() => {
    if (isNavigatingToMessage) {
      scrollManager.beginMessageNavigationScroll()
      wasNavigatingToMessageRef.current = true
      viewerAwayFromBottomRef.current = true
      scrolledConversationRef.current = null
      lastTailMessageKeyRef.current = null
      preferInstantScrollRef.current = false
      return
    }

    if (!wasNavigatingToMessageRef.current || !conversation?.id) {
      return
    }

    wasNavigatingToMessageRef.current = false
    markConversationScrollHandled(conversation.id)
  }, [conversation?.id, isNavigatingToMessage, markConversationScrollHandled, scrollManager])

  // Snap to bottom only when the active conversation changes — not when navigation
  // guards lift (e.g. highlight timeout after opening a notification).
  React.useEffect(() => {
    const conversationId = conversation?.id ?? null

    if (!conversationId) {
      scrolledConversationRef.current = null
      lastTailMessageKeyRef.current = null
      preferInstantScrollRef.current = false
      viewerAwayFromBottomRef.current = false
      prevConversationIdRef.current = null
      return
    }

    const conversationChanged = prevConversationIdRef.current !== conversationId
    prevConversationIdRef.current = conversationId

    if (!conversationChanged) {
      return
    }

    viewerAwayFromBottomRef.current = false

    if (shouldBlockAutoScrollToBottom()) {
      return
    }

    scrolledConversationRef.current = null
    lastTailMessageKeyRef.current = null
    preferInstantScrollRef.current = true
    scrollManager.prepareConversationBottomScroll?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id])

  React.useEffect(() => {
    const conversationId = conversation?.id

    if (!isOpen || !conversationId) {
      scrolledConversationRef.current = null
      return
    }

    const forceBottomSnap = scrollManager.isConversationBottomSnapRequested?.() ?? false

    if (viewerAwayFromBottomRef.current && !forceBottomSnap) {
      return
    }

    if (shouldBlockAutoScrollToBottom()) {
      return
    }

    if (isLoading || messages.length === 0) {
      return
    }

    const messagesBelongToConversation = messages[messages.length - 1]?.conversation?.id === conversationId
    if (!messagesBelongToConversation) {
      return
    }

    if (scrolledConversationRef.current === conversationId && !forceBottomSnap) {
      return
    }

    if (forceBottomSnap) {
      viewerAwayFromBottomRef.current = false
      scrollManager.clearConversationBottomSnapRequest?.()
    }

    markConversationScrollHandled(conversationId)
    scrollManager.scrollToBottomInstant()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, conversation?.id, messages.length, isLoading, markConversationScrollHandled])

  React.useEffect(() => {
    if (
      shouldBlockAutoScrollToBottom() ||
      isLoading ||
      isLoadingPrevious ||
      isLoadingMore ||
      isLoadingPreviousRef.current ||
      isLoadingMoreRef.current ||
      messages.length === 0
    ) {
      return
    }

    if (viewerAwayFromBottomRef.current && !scrollManager.isNearBottom()) {
      return
    }

    const tailKey = getTailMessageKey(messages[messages.length - 1])
    if (tailKey == null) {
      return
    }

    if (lastTailMessageKeyRef.current === null) {
      lastTailMessageKeyRef.current = tailKey
      return
    }

    if (tailKey !== lastTailMessageKeyRef.current) {
      lastTailMessageKeyRef.current = tailKey

      const shouldFollowTail = preferInstantScrollRef.current || scrollManager.isNearBottom()
      if (!shouldFollowTail) {
        return
      }

      if (preferInstantScrollRef.current) {
        preferInstantScrollRef.current = false
        scrollManager.scrollToBottomInstant()
      } else {
        scrollToBottom()
      }
    }
    // Only follow the tail when messages change — not when highlight/navigation guards lift.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isLoading, isLoadingPrevious, isLoadingMore, scrollToBottom, scrollManager])

  React.useEffect(() => {
    if (isLoadingPrevious && !isLoadingPreviousRef.current) {
      isLoadingPreviousRef.current = true
      scrollManager.maintainScrollPosition()
    }
  }, [isLoadingPrevious, scrollManager])

  React.useEffect(() => {
    if (!isLoadingPrevious && isLoadingPreviousRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollManager.restoreScrollPosition()
          isLoadingPreviousRef.current = false
        })
      })
    }
  }, [isLoadingPrevious, scrollManager])

  React.useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore
  }, [isLoadingMore])

  return {
    scrollToBottom: scrollManager.scrollToBottom,
    triggerScrollToBottom: scrollToBottom,
    scrollToBottomInstant: scrollManager.scrollToBottomInstant,
  }
}
